import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useInventoryItems } from '../../../hooks/inventory/useInventoryItems';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InventoryItemView from '../../inventory/modals/InventoryItemView';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import InventoryItems from '../../inventory/modals/InventoryItems';

export default function InventoryItemSearch({addButtonCallback, open, onClose, mode = 'inventory'}) {
    const { t } = useTranslation('common'); 

    const handleClose = () => {
        onClose();
        resetParent();
    };

    const [viewOpen, setViewOpen] = React.useState(false);

    const [selectedItem, setSelectedItem] = React.useState(null);

    const handleListItemClick = (item) => {
      setViewOpen(true);
      setSelectedItem(item);
    };

    const handleAddItemClick = (item) => {
      addButtonCallback(item);
      onClose();
      resetParent();
    };

    const { showSnackbar } = useSnackbar();

    const { items, loading, loadMore, searchFunction, resetParent } = useInventoryItems(open, 1, 10);

    return (
      <React.Fragment>
        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
          <DialogTitle>
          <Stack spacing={2}>
            <Typography variant="h6">Search Inventory Items</Typography>
            <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
            <Divider />
          </Stack>
          </DialogTitle>

          <DialogContent>
            <>
              <List sx={{ minHeight: 500 }}>
                {items?.data?.map((item) => (
                  <ListItem
                      key={item.id}
                      secondaryAction={
                      <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                        <IconButton>
                          {mode === 'inventory' ?
                          <AddShoppingCartIcon 
                            onClick={() => handleAddItemClick(item)}
                            fontSize='large' 
                            color='primary'
                          />
                          : 
                          <AddIcon 
                            onClick={() => handleAddItemClick(item)}
                            fontSize='large' 
                            color='primary'
                          />
                          }
                        </IconButton>
                      </Box>
                      }
                  >
                    <ListItemButton onClick={() => handleListItemClick(item)}>
                      <ListItemText
                      primary={`${item.name}`}
                      secondary={ 
                      <>
                        <Typography component="span" variant="body2" color={Number(item.current_stock) <= Number(item.low_stock) ? "error" : "text.primary" }>
                        {`${removeZeroDecimals(item.current_stock)}${item.uom} in stock`}
                        </Typography><br />
                        <Typography component="span" variant="body2" color="text.secondary">
                        {item.category.name}
                        </Typography>
                      </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  ))}
              </List>

              {/* Load More only shows if more pages exist */}
              {items?.pagination?.page < items?.pagination?.totalPages && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <IconButton 
                      onClick={loadMore}
                      sx={{
                          width: '100%',              // fill container width
                          justifyContent: 'center',   // center the icon horizontally
                          color: 'primary.main',      // use theme's primary color
                      }}
                  >
                      <KeyboardArrowDownIcon />
                  </IconButton>
                </Box>
              )}
              </>
          </DialogContent>
          <DialogActions>
            {mode === 'inventory' ? 
            <InventoryItems mode='button' onCloseCallback={resetParent} />
            : null }
            <div style={{flex: '1 0 0'}} />
            <Button onClick={handleClose}>{ mode === 'inventory' ? "Back to Cart" : "Close" }</Button>
          </DialogActions>
        </Dialog>
        <InventoryItemView 
          item={selectedItem}
          open={viewOpen}
          onClose={() => setViewOpen(false)}
        />
    </React.Fragment>
  );
}